import React, { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Table, TableBody, TableCell, TableContainer, TableRow, useTheme,
} from '@mui/material';
import {
  Cancel, Description, Image, VideoFile,
} from '@mui/icons-material';
import { FileSizeToString } from 'shared/utils';
import { FILE_SIZES } from 'shared/constants';
import { TooltipIconButton } from 'shared/components/abstract/interactables';

// `vars`, not `theme.palette`, which freezes the light values.
const getBaseStyle = (theme) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: (theme.vars || theme).palette.divider,
  borderStyle: 'dashed',
  backgroundColor: (theme.vars || theme).palette.subtleBackground,
  color: (theme.vars || theme).palette.textMuted,
  outline: 'none',
  transition: 'border .24s ease-in-out',
});

function setFileTypeIcon(fileType) {
  switch (fileType) {
    case 'image':
      return <Image />;
    case 'video':
      return <VideoFile />;
    default:
      return <Description />;
  }
}

function fileSizeValidator(file) {
  if (file.size > FILE_SIZES.GB) {
    return {
      code: 'file-too-large',
      message: `File size larger than ${FileSizeToString(FILE_SIZES.GB)} limit`,
    };
  }

  return null;
}

/**
 * @param {Object} props
 * @param {Array<*>} props.pendingFiles
 * @param {Function} props.setPendingFiles
 */
export default function FileDropzone(props) {
  const {
    pendingFiles,
    setPendingFiles,
  } = props;
  const theme = useTheme();

  const onDrop = useCallback((acceptedFiles) => {
    setPendingFiles([...pendingFiles, ...acceptedFiles]);
  }, [pendingFiles, setPendingFiles]);

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    onDrop,
    validator: fileSizeValidator,
  });

  const style = useMemo(() => ({
    ...getBaseStyle(theme),
    ...(isFocused ? { borderColor: (theme.vars || theme).palette.info.main } : {}),
    ...(isDragAccept ? { borderColor: (theme.vars || theme).palette.success.main } : {}),
    ...(isDragReject ? { borderColor: (theme.vars || theme).palette.error.main } : {}),
  }), [theme, isFocused, isDragAccept, isDragReject]);

  const rows = pendingFiles.map((file, index) => ({
    id: index,
    filename: file.name,
    type: file.type.split('/')[0],
    size: FileSizeToString(file.size),
  }));

  const removeFile = (filename) => {
    const arr = pendingFiles.filter((file) => file.name !== filename);
    setPendingFiles(arr);
  };

  return (
    <>
      <div {...getRootProps({
        className: 'dropzone',
        style,
      })}
      >
        <input {...getInputProps()} type="file" />
        {isDragActive ? (
          <p>Release to drop files here and set as selected</p>
        ) : (
          <p>Drag-n-drop files here, or click to select files</p>
        )}

      </div>
      <TableContainer>
        <Table>
          <TableBody>
            {rows.map(({
              filename,
              type,
              size,
            }) => (
              <TableRow key={filename}>
                <TableCell>
                  {setFileTypeIcon(type)}
                  {filename}
                </TableCell>
                <TableCell style={{ minWidth: '80px' }}>{size}</TableCell>
                <TableCell>
                  <TooltipIconButton
                    message="Remove"
                    className="borderless-icon-btn clear"
                    onClick={() => removeFile(filename)}
                    icon={Cancel}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
